import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { useOktaAuth } from '@okta/okta-react'
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef } from 'react'
import { AppShell, getSectionTabs, SectionTabs } from '~/components/shell'
import { activityApi } from '~/lib/api'

export const Route = createFileRoute('/_app')({
  component: AppLayout,
})

// Pure presentational shell: given the current path and the routed children,
// render the DayN top-nav AppShell with the in-context section tabs above the
// page body. No auth hooks live here so it can be rendered in tests directly.
export function AppLayoutView({
  currentPath,
  children,
}: {
  currentPath: string
  children: ReactNode
}) {
  const tabs = getSectionTabs(currentPath)
  return (
    <AppShell currentPath={currentPath}>
      {tabs.length > 0 && <SectionTabs items={tabs} currentPath={currentPath} />}
      {children}
    </AppShell>
  )
}

function AppLayout() {
  const { oktaAuth, authState } = useOktaAuth()
  const navigate = useNavigate()
  const routerState = useRouterState()

  const isAuthenticated = authState?.isAuthenticated
  const isLoading = !authState || authState.isPending

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])

  // Sync Okta access token to localStorage for API calls
  useEffect(() => {
    const syncToken = async () => {
      if (isAuthenticated && authState?.accessToken?.accessToken) {
        // Get access token from authState
        const accessToken = authState.accessToken.accessToken
        localStorage.setItem('auth_token', accessToken)
        console.log('Token synced to localStorage')
      } else if (isAuthenticated) {
        // Fallback: try to get from oktaAuth
        try {
          const accessToken = await oktaAuth.getAccessToken()
          if (accessToken) {
            localStorage.setItem('auth_token', accessToken)
            console.log('Token synced to localStorage (fallback)')
          } else {
            console.warn('No access token available from Okta')
          }
        } catch (e) {
          console.error('Failed to get access token:', e)
        }
      }
    }
    syncToken()
  }, [isAuthenticated, authState?.accessToken, oktaAuth])

  // Track page visits
  const lastTrackedPath = useRef<string>('')
  useEffect(() => {
    const trackPageVisit = async () => {
      if (!isAuthenticated || !authState?.accessToken?.accessToken) {
        return
      }

      const path = routerState.location.pathname
      // Only track if the path has changed (avoid duplicate tracking)
      if (path === lastTrackedPath.current) {
        return
      }
      lastTrackedPath.current = path

      // Get page title from route or use path
      const pageTitle = document.title || path

      try {
        await activityApi.log(path, pageTitle)
      } catch (error) {
        // Silently fail - don't break the app if tracking fails
        console.debug('Activity tracking failed:', error)
      }
    }

    trackPageVisit()
  }, [routerState.location.pathname, isAuthenticated, authState?.accessToken?.accessToken])

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <Box textAlign="center">
          <Spinner size="xl" color="blue.500" />
          <Text mt={4} color="gray.500">
            Loading...
          </Text>
        </Box>
      </Flex>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  const currentPath = routerState.location.pathname

  return (
    <AppLayoutView currentPath={currentPath}>
      <Outlet />
    </AppLayoutView>
  )
}
